import { useEffect } from 'react';
import { globalTracer } from 'opentracing';
import { getSessionId, SESSION_ID_TAG } from './session-id';
import { Tags, Logs } from '@spotify-internal/tracing-web';

// TODO: we may need to turn this Type into a JS structure to enable runtime
// validation for non-TS components.

// The configuration for breadcrumb span names and their required (or not tags).
// A value of `null` in `tags` means there are none required. An `error` tag is
// treated particularly special in the Lightstep UI, and denotes that an actual
// error has occured.
type BreadcrumbConfig = {
  // Only to be used when testing ErrorBoundary itself.
  'for-tests-only': {
    tags: {
      error: true;
    };
  };

  navigation: {
    tags: {
      'nvgtn.from_uri': string | null;
      'nvgtn.to_uri': string | null;
    };
  };

  'app-mount': {
    tags: null;
  };

  'app-error': {
    tags: {
      error: true;
    };
  };

  's4a-page-body': {
    tags: {
      error: true;
    };
  };

  's4a-page-header': {
    tags: {
      error: true;
    };
  };

  'nms-errorboundary': {
    tags: {
      error: true;
    };
  };

  's4a-sidepanel': {
    tags: {
      error: true;
    };
  };

  // CANVAS_SONG_ENTITY
  // Not referencing directly to avoid referencing the feature
  'canvas-song-entity': {
    tags: {
      error: true;
    };
  };

  // CANVAS_SLO_NAME
  // Not referencing directly to avoid referencing the feature
  ['canvas-upcoming-dialog']: {
    tags: {
      error: true;
    };
  };

  'enhance-upcoming-release': {
    tags: {
      error: true;
    };
  };

  // Roster view
  'roster-view': {
    tags: {
      error: true;
    };
  };

  'artist-fundraising-pick': { tags: { error: true } };

  's4a-accept-terms': {
    tags: {
      error: true;
    };
  };
};

// Create some helper types to separate out "events" (non-errors) from "errors"
// (error: true in tags).
export type BreadcrumbEvents = {
  [K in keyof BreadcrumbConfig]: BreadcrumbConfig[K] extends {
    tags: { error: true };
  }
    ? never
    : K;
}[keyof BreadcrumbConfig];
export type BreadcrumbErrors = {
  [K in keyof BreadcrumbConfig]: BreadcrumbConfig[K] extends {
    tags: { error: true };
  }
    ? K
    : never;
}[keyof BreadcrumbConfig];

// Create a subtype of all the configs where `tags: null`, denoting that the
// tags prop is not required for those calls.
type WithNullTags = {
  [K in keyof BreadcrumbConfig]: BreadcrumbConfig[K] extends { tags: null }
    ? K
    : never;
}[keyof BreadcrumbConfig];

/**
 * Mark that a specific event has happened. In Lightstep, it will appear as
 * `breadcrumb.[name]`. Any Breadcrumb can optionally contain key-value
 * longer-form data (a log) that will appear in Lightstep. This is mostly useful
 * for more contextual, but non-queryable data, like user agent, or a stack trace.
 */

// Overload 1: only the name is required
export function Breadcrumb<Name extends WithNullTags>(props: {
  name: Name;
  logs?: { [key: string]: any };
}): null;
// Overload 2: Name and Tags are required
export function Breadcrumb<
  Name extends keyof BreadcrumbConfig,
  Tags extends BreadcrumbConfig[Name]['tags']
>(props: { name: Name; tags: Tags; logs?: { [key: string]: any } }): null;
// The implementation
export function Breadcrumb<
  Name extends keyof BreadcrumbConfig,
  Tags extends BreadcrumbConfig[Name]['tags'],
  Logs extends { [key: string]: string | boolean | null }
>({ name, tags, logs }: { name: Name; tags?: Tags; logs?: Logs }) {
  const hash = JSON.stringify({ name, tags, logs });

  useEffect(() => {
    // This is just to have a unique value for useEffect, but not need to
    // disable the exhaustive deps rule.
    const reparsed: {
      name: Name;
      tags?: Tags;
      logs?: Logs;
    } = JSON.parse(hash);

    // TODO: consider exposing a startTime and endTime.
    const operation = `breadcrumb.${reparsed.name}`;
    const span = globalTracer().startSpan(operation, {
      tags: {
        [SESSION_ID_TAG]: getSessionId(),
        [Tags.OPERATION_KIND]: 'breadcrumb',
        // it's fine to spread `undefined`, apparently
        ...reparsed.tags,
      },
    });

    if (reparsed.logs) {
      span.log(reparsed.logs);
    }

    span.finish();
  }, [hash]);

  return null;
}

export function errorAsLog(err: Error) {
  return {
    [Logs.EVENT]: 'error',
    [Logs.ERROR_OBJECT]: err,
    [Logs.ERROR_KIND]: err.name,
    [Logs.MESSAGE]: err.message,
    [Logs.STACK]: err.stack,
  };
}
